import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import SEOHead from "@/components/base/SEOHead";
import SchemaOrg from "@/components/base/SchemaOrg";
import Breadcrumb from "@/components/base/Breadcrumb";
import { categoryData } from "@/mocks/categoryData";

type CategoryKey = "sports" | "live" | "slots";

const categoryKeys: { key: CategoryKey; label: string; icon: string }[] = [
  { key: "sports", label: "体育竞技", icon: "ri-football-line" },
  { key: "live", label: "真人娱乐", icon: "ri-live-line" },
  { key: "slots", label: "电子游艺", icon: "ri-gamepad-line" },
];

const categoryMeta: Record<CategoryKey, { title: string; desc: string; keywords: string }> = {
  sports: {
    title: "体育竞技 - 必赢亚洲",
    desc: "必赢亚洲体育竞技平台，覆盖全球主流赛事，实时赔率更新。足球、篮球、网球、电竞等热门项目一应俱全，支持赛前投注和滚球投注。",
    keywords: "必赢亚洲体育投注,体育竞技,足球投注,篮球投注,电竞投注,赛事赔率",
  },
  live: {
    title: "真人娱乐 - 必赢亚洲",
    desc: "必赢亚洲真人娱乐平台，全球顶级真人荷官在线发牌，高清4K直播无延迟。百家乐、轮盘、骰宝、龙虎斗等经典玩法随时畅享。",
    keywords: "必赢亚洲真人娱乐,百家乐,轮盘,骰宝,真人荷官,在线娱乐场",
  },
  slots: {
    title: "电子游艺 - 必赢亚洲",
    desc: "必赢亚洲电子游艺平台，汇聚全球顶尖游戏开发商精品力作，超过800款电子游艺任您选择。老虎机、捕鱼、棋牌等丰富选择应有尽有。",
    keywords: "必赢亚洲电子游艺,老虎机,捕鱼游戏,棋牌游戏,在线游戏",
  },
};

export default function CategoryPage() {
  const { category } = useParams<{ category: string }>();
  const [activeKey, setActiveKey] = useState<CategoryKey>("sports");

  useEffect(() => {
    if (category && ["sports", "live", "slots"].includes(category)) {
      setActiveKey(category as CategoryKey);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [category]);

  const currentCategory = categoryData[activeKey];
  const meta = categoryMeta[activeKey];

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: currentCategory.name },
  ];

  return (
    <>
      <SEOHead
        title={meta.title}
        description={meta.desc}
        keywords={meta.keywords}
        canonicalUrl={`https://www.biyingyazhou.com/category/${activeKey}`}
      />
      <SchemaOrg
        data={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": meta.title,
          "description": meta.desc,
          "url": `https://www.biyingyazhou.com/category/${activeKey}`,
          "inLanguage": "zh-CN",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://www.biyingyazhou.com/",
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": currentCategory.name,
              },
            ],
          },
        }}
      />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <div className="relative h-56 md:h-72 overflow-hidden">
          <img
            src={currentCategory.image}
            alt={currentCategory.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/70"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <i className={`${currentCategory.icon} text-lg`}></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-background-50">
                  {currentCategory.name}
                </h1>
              </div>
              <p className="text-sm text-background-300 max-w-2xl">
                {currentCategory.description}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-background-100 border-b border-background-200 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 py-3 overflow-x-auto">
              {categoryKeys.map((cat) => (
                <Link
                  key={cat.key}
                  to={`/category/${cat.key}`}
                  className={`flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeKey === cat.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70"
                  }`}
                >
                  <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                  {cat.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {currentCategory.games.map((game) => (
                <div
                  key={game.id}
                  className="group bg-background-100 rounded-lg p-5 border border-background-200/70 hover:border-primary-500/20 transition-all duration-300 cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500/10 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                        <i className={`${game.icon} text-lg`}></i>
                      </div>
                      <div>
                        <h3 className="text-base font-heading font-bold text-foreground-900">
                          {game.name}
                        </h3>
                        {game.provider && (
                          <span className="text-xs text-foreground-500">{game.provider}</span>
                        )}
                      </div>
                    </div>
                    {game.hot && (
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-accent-500 text-background-50">
                        热门
                      </span>
                    )}
                  </div>

                  <div className="space-y-1.5 text-sm">
                    {game.leagues && (
                      <div className="flex items-center gap-2">
                        <span className="text-foreground-500">覆盖联赛:</span>
                        <span className="text-foreground-700">{game.leagues}</span>
                      </div>
                    )}
                    {game.tables && (
                      <div className="flex items-center gap-2">
                        <span className="text-foreground-500">桌台类型:</span>
                        <span className="text-foreground-700">{game.tables}</span>
                      </div>
                    )}
                    {game.odds && (
                      <div className="flex items-center gap-2">
                        <span className="text-foreground-500">赔率范围:</span>
                        <span className="text-primary-600 font-semibold">{game.odds}</span>
                      </div>
                    )}
                    {game.limit && (
                      <div className="flex items-center gap-2">
                        <span className="text-foreground-500">投注限额:</span>
                        <span className="text-primary-600 font-semibold">{game.limit}</span>
                      </div>
                    )}
                    {game.rtp && (
                      <div className="flex items-center gap-2">
                        <span className="text-foreground-500">RTP:</span>
                        <span className="text-accent-600 font-semibold">{game.rtp}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}